import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { CardConfig } from './cardConfig'
import { OverviewCard } from './OverviewCard'
import { OpenEntityCanvasAction } from '../types'
import {
  ControlledCheckbox,
  stateTransition,
} from '@/Atomic/Atoms/Inputs/ControlledCheckbox'
import { DisplayCountIndicator } from '@/Components/DisplayCountIndicator'

import './CardView.scss'

interface OverviewCardListProps {
  dataItems: DataItem[]
  schema: DataItem
  config: CardConfig
  onOpenEntityCanvasCallbackFn?: OpenEntityCanvasAction
  onOpenDataItemDrawerClick: (
    e: React.MouseEvent,
    dataItem: DataItem,
    startInEdit?: boolean,
  ) => void
  selectRow: (id: string, select?: boolean) => void
  isRowSelected: (id: string) => boolean
  isRowSelectDisabled: (id: string) => boolean
  selectAllRows: (select?: boolean) => void
  isAllRowsSelected: () => boolean | undefined
  isAllRowsSelectDisabled: () => boolean
  hasPopup?: boolean
}

/**
 * Phone/tablet representation of an entity overview: an optional select-all
 * header (for selectable entities) followed by one {@link OverviewCard} per
 * row, laid out from the entity's {@link CardConfig}. Rendered by
 * {@link EntityTable} below the tablet breakpoint in place of the table; the
 * surrounding bulk-action popup and drawers stay owned by EntityTable so the
 * card and table views share identical multi-select and drawer state.
 */
export const OverviewCardList: React.FC<OverviewCardListProps> = ({
  dataItems,
  schema,
  config,
  onOpenEntityCanvasCallbackFn,
  onOpenDataItemDrawerClick,
  selectRow,
  isRowSelected,
  isRowSelectDisabled,
  selectAllRows,
  isAllRowsSelected,
  isAllRowsSelectDisabled,
  hasPopup,
}) => {
  const { t } = useTranslation()

  return (
    <div
      className={classNames('overview-card-view', { 'has-popup': hasPopup })}
    >
      <div className="overview-card-select-header">
        {config.selectable && (
          <label className="overview-card-select-header-title">
            <ControlledCheckbox
              checked={isAllRowsSelected()}
              disabled={isAllRowsSelectDisabled()}
              onChange={() =>
                selectAllRows(stateTransition(isAllRowsSelected()))
              }
            />
            <span className="overview-card-select-header-label">
              {t('overview.card.selectAll')}
            </span>
          </label>
        )}
        <DisplayCountIndicator />
      </div>

      <div className="overview-card-list">
        {dataItems.map((dataItem) => (
          <OverviewCard
            key={dataItem.id()}
            dataItem={dataItem}
            schema={schema}
            config={config}
            onOpenEntityCanvasCallbackFn={onOpenEntityCanvasCallbackFn}
            onOpenDataItemDrawerClick={onOpenDataItemDrawerClick}
            selectRow={selectRow}
            isRowSelected={isRowSelected}
            isRowSelectDisabled={isRowSelectDisabled}
          />
        ))}
      </div>
    </div>
  )
}
